<template>
    <!-- Chart Content Wrapper -->
    <div v-if="allDisplayData.length > 0" ref="chartContentWrapperRef" class="chart-content-wrapper">
        <!-- Total Amounts Text Blocks -->
        <div v-if="selectedCurrency !== 'allCurrencies'" class="total-amounts-text-blocks-wrapper">
            <!-- Assets vs Liabilities Total Text Block -->
            <div v-if="selectedTab === 'assetsVsLiabilities'" class="total-assets-vs-liabilities-text-blocks">
                <text-block-generic
                :params="totalAssetsTextBlockParams"
                :icon="totalAssetsTextBlockParams.icon"
                :colorIcon="totalAssetsTextBlockParams.colorIcon"
                applyEllipsis />
                <text-block-generic
                :params="totalLiabilitiesTextBlockParams"
                :icon="totalLiabilitiesTextBlockParams.icon"
                :colorIcon="totalLiabilitiesTextBlockParams.colorIcon"
                applyEllipsis />
            </div>
            <!-- Product Total Text Block -->
            <text-block-generic
            v-else-if="selectedProduct !== 'allProducts'"
            :params="productTotalTextBlockParams"
            :icon="productTotalTextBlockParams.icon"
            :colorIcon="productTotalTextBlockParams.colorIcon"
            applyEllipsis />
        </div>
        <!-- Chart -->
        <div class="chart-wrapper" :key="selectedCurrency + selectedProduct + selectedTab + canvasWidth + getAppThemeMode">
            <chart-complex
            v-if="!hideChart"
            identifier="patrimony"
            :graphData="graphData"
            :colors="hideLabels ? [notSelectedColor] : colorsForGraph"
            :hideLabels="hideLabels"
            :hoverIndex="hoverIndex"
            :showZeroTooltip="showZeroTooltip"
            :clusterTooltip="labelHoverIndex === 'clusterAccounts'"
            :canvasWidth="canvasWidth"
            @mouseHover="handleGraphHover($event)"
            @mouseOut="hasLabelOver = false"
            @sliceClicked="handleSliceClick($event)">
                <div
                v-if="selectedProduct"
                class="chart-tooltips-wrapper">
                    <!-- Tooltip component to display element hovered info -->
                    <tooltip
                    v-if="tooltipInfo && labelHoverIndex !== 'clusterAccounts'"
                    :key="labelHoverIndex"
                    :colors="colors"
                    :labelHoverIndex="labelHoverIndex"
                    :tooltipInfo="tooltipInfo"
                    :selectedProduct="selectedProduct"/>
                    <!-- Cluster Tooltip component to display element hovered info -->
                    <tooltip-cluster
                    v-if="labelHoverIndex === 'clusterAccounts'"
                    :clusterAccountsLength="clusterLength"
                    :key="labelHoverIndex"
                    @changeToTableView="$emit('changeToTableView')"/>
                </div>
            </chart-complex>
        </div>
        <!-- Created a string to use as key, to detect change at our display data, forcing this to reRender and apply masks -->
        <div
        v-if="allDisplayData.length"
        class="graph-labels"
        :class="{'has-label-hovered': hasLabelOver}"
        :key="JSON.stringify(allDisplayData)">
            <div
            v-for="(item, index) in allDisplayData"
            :key="item[0]"
            class="label"
            :class="{'hover-on-me': hasLabelOver && index === labelHoverIndex}"
            @mouseout="mouseouthandler(item)"
            @mouseover="handleLabelHover(item)"
            @click="handleLabelClick(item)">
                <div class="color-square" :style="{backgroundColor: colors[index]}"></div>
                <div class="item-label" v-if="selectedProduct === 'allProducts'">{{item.description}}</div>
                <div class="item-label" v-if="selectedProduct !== 'allProducts' && item.accountDescription">{{item.accountDescription}}</div>
                <div class="item-label" v-else-if="item.accountNumber">{{accountFormatHelper(item.accountNumber)}}</div>
                <div class="item-percentage">{{item.percentage !== 0 && item.parsedPercentage === 0 ? '≈ ' : ''}}{{item.parsedPercentage}}%</div>
            </div>
        </div>
    </div>
</template>

<script setup>
import ChartComplex from '@/components/generic/charts/Complex'
import Tooltip from '@/components/features/accounts/patrimony/chart/desktop/Tooltip'
import TooltipCluster from '@/components/features/accounts/patrimony/chart/desktop/TooltipCluster'
import TextBlockGeneric from '@/components/generic/textBlocks/Generic'
/* Vue & Assets */
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { navigateByName } from '@/router/helpers/navigation'
import { getAccountRouteName } from '@/assets/scripts/mappers/accounts'
import {
    calculateActivePassivePercentage, createActivePassiveGraphArray, calculateProductTotalAmount, calculateProductTotalAmountByCurrency, calculateProductPercentage, createProductGraphArray, calculateArrayAccountsPercentage, createAccountGraphArray,
    convertHexToHSL, createColorBasedArray
} from '@/assets/scripts/parsers/patrimony'
import { accountFormatHelper } from '@/assets/scripts/strings'
import { amountFormat } from '@/assets/scripts/amounts'
/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['resetPickerDrops', 'productChanged', 'updateSelectedTab', 'showEmptyState'])

/** props received
@selectedCurrency {string} - selected currency in our picker
@selectedProduct {string} - selected product in our picker
@savedBackState {Object} - saved back state
@selectedTab {String, Number}
*/
const props = defineProps({
    selectedCurrency: String,
    selectedProduct: String,
    savedBackState: Object,
    selectedTab: [String, Number]
})

const { savePatrimonyBackState } = mapActions(moduleMapper.ACCOUNTS_PATRIMONY)
const { getAccountsProductsList, getPatrimonyAccountsList } = mapGetters(moduleMapper.ACCOUNTS_PATRIMONY)
const { getAppThemeMode } = mapGetters()

/**********************************************************************
*                                                                     *
*                             Charts Logic                            *
*                                                                     *
**********************************************************************/
const clusterLength = ref(0)
const showZeroTooltip = ref(false)
const hasLabelOver = ref(false)
const hoverIndex = ref(-1)
const labelHoverIndex = ref(null)
const notSelectedColor = ref('#ebf1f3')
const assetsVsLiabilitiesColors = ref(['var(--cashflow-entry)', 'var(--cashflow-exit)'])
const hideChart = ref(true)
const chartContentWrapperRef = ref('')
const canvasWidth = ref(null)

const allDisplayData = computed(() => {
    const selectedCurrency = props.selectedCurrency === 'allCurrencies' ? null : props.selectedCurrency
    const selectedProduct = props.selectedProduct === 'allProducts' ? null : props.selectedProduct

    if (props.selectedTab === 'assetsVsLiabilities') {
        return getAccountFamilyGraph('labels', selectedCurrency)
    } else {
        if (selectedProduct) {
            return getAccountsGraph('labels', selectedCurrency, selectedProduct)
        } else {
            return getProductGraph('labels', selectedCurrency, props.selectedTab)
        }
    }
})

const graphData = computed(() => {
    const selectedCurrency = props.selectedCurrency === 'allCurrencies' ? null : props.selectedCurrency
    const selectedProduct = props.selectedProduct === 'allProducts' ? null : props.selectedProduct

    if (props.selectedTab === 'assetsVsLiabilities') {
        return getAccountFamilyGraph('chart', selectedCurrency)
    } else {
        if (selectedProduct) {
            return getAccountsGraph('chart', selectedCurrency, selectedProduct)
        } else {
            return getProductGraph('chart', selectedCurrency, props.selectedTab)
        }
    }
})

const hideLabels = computed(() => {
    return graphData.value.length && graphData.value[0][0] === 'empty'
})

/*
* We need to define our colors array based on:
*	- accountFinancialType being 'assetsVsLiabilities', it only has positive or negative color
*	- allProducts option, we create an array with only the colors of products available for this filter
*	- specific product, we create an array with the product color as base
*/
const colors = computed(() => {
    if (props.selectedTab === 'assetsVsLiabilities') return assetsVsLiabilitiesColors.value

    return props.selectedProduct === 'allProducts' ? createProductsPalette() : createAccountsPalette()
})

/*
* We need to have a diferent array for graph, since we only show positive values
*/
const colorsForGraph = computed(() => {
    if (props.selectedTab === 'assetsVsLiabilities') return assetsVsLiabilitiesColors.value

    return props.selectedProduct === 'allProducts' ? createProductsPalette(true) : createAccountsPalette(true)
})

/*
* Getting info from our allDisplayData that matches our hovered element
*/
const tooltipInfo = computed(() => {
    return allDisplayData.value[labelHoverIndex.value]
})

/** Params for total assets text block */
const totalAssetsTextBlockParams = computed(() => {
    const totalAmount = calculateProductTotalAmountByCurrency(getPatrimonyAccountsList.value(props.selectedCurrency, props.selectedProduct, 'assets'))

    return {
        label: `${t('accounts.patrimony.labels.totalAssets')}`,
        content: amountFormat(Math.abs(totalAmount), props.selectedCurrency),
        icon: totalAmount !== 0 ? totalAmount > 0 ? 'plus' : 'minus' : null,
        colorIcon: totalAmount > 0 ? 'var(--positive)' : 'var(--negative)',
        classes: 'amount-icon'
    }
})

/** Params for total liabilities text block */
const totalLiabilitiesTextBlockParams = computed(() => {
    const totalAmount = calculateProductTotalAmountByCurrency(getPatrimonyAccountsList.value(props.selectedCurrency, props.selectedProduct, 'liabilities'))

    return {
        label: `${t('accounts.patrimony.labels.totalLiabilities')}`,
        content: amountFormat(Math.abs(totalAmount), props.selectedCurrency),
        icon: totalAmount !== 0 ? totalAmount > 0 ? 'plus' : 'minus' : null,
        colorIcon: totalAmount > 0 ? 'var(--positive)' : 'var(--negative)',
        classes: 'amount-icon'
    }
})

/** Params for product total amount text block */
const productTotalTextBlockParams = computed(() => {
    const productDescription = getAccountsProductsList.value(null, getAppThemeMode.value).find(product => product.id === props.selectedProduct).description
    const totalAmount = calculateProductTotalAmount(getPatrimonyAccountsList.value(props.selectedCurrency, props.selectedProduct))

    return {
        label: `${t('accounts.patrimony.labels.total')} ${productDescription}`,
        content: amountFormat(Math.abs(totalAmount), props.selectedCurrency),
        icon: totalAmount !== 0 ? totalAmount > 0 ? 'plus' : 'minus' : null,
        colorIcon: totalAmount > 0 ? 'var(--positive)' : 'var(--negative)',
        classes: 'amount-icon'
    }
})

/*
* Graphic to compare assets and liabilities
*/
const getAccountFamilyGraph = (displayType, selectedCurrency) => {
    let totalAmount = 0
    let assetsTotal = 0
    let liabilitiesTotal = 0

    getPatrimonyAccountsList.value(selectedCurrency, null, 'assets').forEach(account => {
        assetsTotal += account.convertedBalance
        totalAmount += account.convertedBalance
    })

    getPatrimonyAccountsList.value(selectedCurrency, null, 'liabilities').forEach(account => {
        liabilitiesTotal += Math.abs(account.convertedBalance)
        totalAmount += Math.abs(account.convertedBalance)
    })

    // We have to create this empty array, to be display "empty" chart when we have values, but their total % equals 0
    if (totalAmount === 0 && assetsTotal === 0 && liabilitiesTotal === 0) return [['empty', 100]]

    const graphArray = [
        {
            id: 0,
            description: t('accounts.patrimony.labels.assets'),
            totalAmount: assetsTotal,
            accounts: getPatrimonyAccountsList.value(null, null, 'assets')
        },
        {
            id: 1,
            description: t('accounts.patrimony.labels.liabilities'),
            totalAmount: liabilitiesTotal,
            accounts: getPatrimonyAccountsList.value(null, null, 'liabilities')
        }
    ]

    return displayType === 'chart'
        ? createActivePassiveGraphArray(calculateActivePassivePercentage(graphArray, totalAmount))
        : calculateActivePassivePercentage(graphArray, totalAmount)
}

/*
* Graphic to show a single Product based in selectedCurrency and accountFinancialType
* This chart will show accounts with that product
*/
const getAccountsGraph = (displayType, selectedCurrency, selectedProduct) => {
    const graphArray = []
    const accountsList = getPatrimonyAccountsList.value(selectedCurrency, selectedProduct)
    let totalAmount = 0

    if (accountsList.length) {
        accountsList.forEach(account => {
            totalAmount += account.convertedBalance
            graphArray.push(account)
        })
    }

    // We have to create this empty array, to be display "empty" chart when we have values, but their total % equals 0
    if (totalAmount === 0 && displayType === 'chart') return [['empty', 100]]

    let graphData
    if (displayType === 'chart') {
        graphData = createAccountGraphArray(calculateArrayAccountsPercentage(graphArray, totalAmount)).graph
        clusterLength.value = createAccountGraphArray(calculateArrayAccountsPercentage(graphArray, totalAmount)).cluster.length
    }
    return displayType === 'chart' ? graphData : calculateArrayAccountsPercentage(graphArray, totalAmount)
}

/*
* Graphic with all Products based in selectedCurrency and accountFinancialType
*/
const getProductGraph = (displayType, selectedCurrency, accountFinancialType) => {
    const graphArray = []
    let totalAmount = 0

    getAccountsProductsList.value(null, getAppThemeMode.value).forEach(product => {
        if (getPatrimonyAccountsList.value(selectedCurrency, product.id, accountFinancialType).length) {
            const productInfo = {
                id: product.id,
                color: product.color,
                description: product.description,
                accounts: getPatrimonyAccountsList.value(selectedCurrency, product.id, accountFinancialType)
            }

            productInfo.totalAmount = calculateProductTotalAmount(productInfo.accounts)
            totalAmount += productInfo.totalAmount
            graphArray.push(productInfo)
        }
    })

    // We have to create this empty array, to be display "empty" chart when we have values, but their total % equals 0
    if (totalAmount === 0 && displayType === 'chart') return [['empty', 100]]

    return displayType === 'chart' ? createProductGraphArray(calculateProductPercentage(graphArray, totalAmount)) : calculateProductPercentage(graphArray, totalAmount)
}

/**
* Creates an array with only the colors of products available for this filter
* We use allDisplayData, cuz it has all the info including products with 0%
@onlyPositiveValues {Boolean} - used to filter colors of products with 0%, used for chart since it only receives positive products
*/
const createProductsPalette = onlyPositiveValues => {
    if (onlyPositiveValues) return allDisplayData.value.filter(obj => obj.percentage !== 0).map(obj => { return obj.color })

    return allDisplayData.value.map(obj => { return obj.color })
}

/**
* Creates an array with with selected product as the base
* We use allDisplayData, cuz it has all the info including accounts with 0%
@onlyPositiveValues {Boolean} - used to filter colors of accounts with 0%, used for chart since it only receives positive accounts
*/
const createAccountsPalette = onlyPositiveValues => {
    const baseColor = getAccountsProductsList.value(null, getAppThemeMode.value).find(obj => obj.id === props.selectedProduct).color
    const hslBaseColor = convertHexToHSL(baseColor)
    const auxColor = createColorBasedArray(hslBaseColor, allDisplayData.value.length)
    const auxData = allDisplayData.value.map((obj, index) => {
        return { ...obj, color: auxColor[index] }
    })

    if (onlyPositiveValues) return auxData.filter(obj => obj.percentage !== 0).map(obj => { return obj.color })

    return auxColor
}

/**********************************************************************
*                                                                     *
*                     Handle for Chart Hovers&Clicks                  *
*                                                                     *
**********************************************************************/
/*
* We only send hoverIndex to chart when percentage !== 0%
* When is 0%, we need to show tooltip without trigger chart mouseover
*/
const handleLabelHover = (item) => {
    labelHoverIndex.value = allDisplayData.value.findIndex(obj => obj.id === item.id)
    hasLabelOver.value = true

    if (item.percentage > 1) {
        hoverIndex.value = graphData.value.findIndex(array => array[2] === (props.selectedProduct === 'allProducts' ? item.description : item.accountNumber))
    } else {
        showZeroTooltip.value = true
    }
}

const mouseouthandler = () => {
    showZeroTooltip.value = false
    hoverIndex.value = -1
    hasLabelOver.value = false
}
/*
* Validation which label of all data is the right one
*/
const handleGraphHover = ($event) => {
    /* No point in hovering chart to display tooltip, we don't even know which of the accounts to show, cuz there is no slice for it */
    if ($event.data.label === 'EMPTY') return

    hasLabelOver.value = true

    if (props.selectedProduct === 'allProducts') {
        labelHoverIndex.value = allDisplayData.value.findIndex(obj => {
            return obj.description?.toLowerCase() === $event.data.sliceId?.toLowerCase()
        })
    } else {
        /* when whe have clusterAccounts  we need to create a diferent tooltip */
        if ($event.data.sliceId === 'clusterAccounts') {
            labelHoverIndex.value = 'clusterAccounts'
        } else {
            labelHoverIndex.value = allDisplayData.value.findIndex(obj => {
                return obj.accountNumber?.toLowerCase() === $event.data.sliceId?.toLowerCase()
            })
        }
    }
}

/*
* Handling click on slice we need to see if we have products or accounts
* when clicking a product, we will select it
* when clicking an account, we will go to balances(or detail) page relative to that account
*/
const handleSliceClick = $event => {
    // if we are viewing charts for assetsVsLiabilities there's no action for this click
    if (props.selectedTab === 'assetsVsLiabilities') return

    const item = $event.data

    if (props.selectedProduct === 'allProducts') {
        const productId = allDisplayData.value.find(obj => obj.description.toLowerCase() === item.label.toLowerCase()).id
        emit('productChanged', productId)
    } else {
        const account = allDisplayData.value.find(obj => obj.accountNumber.toLowerCase() === item.sliceId.toLowerCase())
        handleAccountClick(account)
    }
}

const handleLabelClick = item => {
    // if we are viewing charts for assetsVsLiabilities there's no action for this click
    if (props.selectedTab === 'assetsVsLiabilities') return

    props.selectedProduct === 'allProducts' ? emit('productChanged', item.id) : handleAccountClick(item)
}

const handleAccountClick = account => {
	navigateByName(getAccountRouteName(account.accountType), { account: account.id })
    savePatrimonyBackState(props.savedBackState)
}
/**********************************************************************
*                                                                     *
*                                 WATCHS                              *
*                                                                     *
**********************************************************************/
watch(() => props.selectedTab, (newVal, oldVal) => {
    if (newVal === 'assetsVsLiabilities') emit('resetPickerDrops')
})

watch(() => props.selectedProduct, () => {
    emit('updateSelectedTab', 'assets')
    checkWidgetSize()
})

watch(() => props.selectedCurrency, () => {
    if (allDisplayData.value.length === 0) emit('showEmptyState')
})

const checkWidgetSize = () => {
    hideChart.value = true
    setTimeout(() => {
        canvasWidth.value = chartContentWrapperRef.value.clientWidth
        hideChart.value = false
    })
}

onMounted(() => {
    if (allDisplayData.value.length === 0) emit('showEmptyState')

    if (props.savedBackState && props.savedBackState.tabSelected) emit('updateSelectedTab', props.savedBackState.tabSelected)
    canvasWidth.value = chartContentWrapperRef.value.clientWidth
    hideChart.value = false
})
</script>

<style lang="scss" scoped>
.generic-tabs-wrapper {
    width: auto;
    margin: 12px 0 16px 0;
}

.patrimony-empty-state {
    @include commonEnteringAnimation;
}

.chart-content-wrapper {
    position: relative;

    width: 100%;

	padding: 8px 7px;

    .total-amounts-text-blocks-wrapper {
        position: absolute;
        top: 0;
        left: 0;

        .text-blocks-wrapper {
            margin-bottom: 24px;

            :deep(.content-wrapper) {
                svg {
                    width: 16px;
                    height: 16px;
                }
            }
        }
    }

    .chart-wrapper {
        width: 100%;

        display: flex;
        align-items: center;
        justify-content: center;
    }

    .graph-labels {
        width: 100%;
        padding: 0 8px;

        display: flex;
        flex-direction: column;
        gap: 16px;

        padding-top: 10px;

        &.has-label-hovered {
            .label:not(.hover-on-me) {
                .color-square,
                .item-percentage {
                    opacity: 0.16;
                }
            }
        }

        .label {
            width: 100%;

            display: flex;
            flex-direction: row;
            align-items: center;

            @include H7-Regular;

            cursor: pointer;

            .color-square {
                width: 16px;
                height: 16px;
                margin-right: 8px;
                border-radius: 50%;

                flex-shrink: 0;
            }

            .item-label {
                color: var(--secondary-dark-4);

                margin-right: 4px;

                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }

            .item-percentage {
                color: var(--secondary-dark-1)
            }
        }
    }
}
</style>
